import React, { useMemo } from "react";
import { DropEvent } from "react-dropzone";
import { Form } from "react-final-form";
import { Box, Button, Typography } from "@mui/material";

import { LoadingButton } from "ui";

import { DropzoneField } from "fond/form/fields";
import RequiredFiles from "fond/form/fields/RequiredFiles/RequiredFiles";
import { ProjectSubtype } from "fond/types";
import { flattenFolderStructureFromFileList, getFilesFromEvent } from "fond/utils/files";
import { requireAll } from "fond/utils/validation";
import { Modal } from "fond/widgets";
import { validFileName } from "fond/widgets/Dropzone/utils";

import { getNBNFiles } from "./files";
import { ProjectCodeField } from "./ProjectCodeField";
import { UpdateVersionReturn } from "./types";

interface UploadS18ModalProps {
  type: ProjectSubtype;
  projectCode?: string | null;
  onClose: () => void;
  onSubmit: (values: UploadFormData) => void;
  handleUpdateProjectCode: (projectCode: string) => Promise<UpdateVersionReturn>;
}

export interface UploadFormData {
  ProjectCode: string;
  Files: File[];
}

const getFilesFromEventWithFlattenedStructure = async (event: DropEvent): Promise<File[]> => {
  const files = await getFilesFromEvent(event);
  return flattenFolderStructureFromFileList(files);
};

const UploadS18Modal: React.FC<UploadS18ModalProps> = ({ type, projectCode, onClose, onSubmit, handleUpdateProjectCode }: UploadS18ModalProps) => {
  // The list of required files for the selected project type
  const s18Files = useMemo(() => getNBNFiles(type), [type]);

  return (
    <Modal
      open
      variant="primary"
      header="Upload input data"
      content={
        <Box mt={2} height="50%">
          <Form<UploadFormData>
            validateOnBlur
            onSubmit={onSubmit}
            subscription={{ submitting: true, valid: true, dirty: true }}
            render={({ handleSubmit, submitting, valid, dirty, form }) => {
              return (
                <form onSubmit={handleSubmit}>
                  <Box my={2} display="flex" flexDirection="column" rowGap={3} columnGap={6}>
                    <Box>
                      <ProjectCodeField projectCode={projectCode} handleUpdateProjectCode={handleUpdateProjectCode} />
                      <Typography variant="caption" color="textSecondary">
                        This value will be used to determine which design elements are in scope.
                      </Typography>
                    </Box>
                    <DropzoneField
                      name="Files"
                      acceptedFiles={{
                        "application/json": [".dat", ".id", ".map", ".tab"],
                        "application/zip": [".zip"],
                        // Windows uploads .zip files with the non-standard MIME type application/x-zip-compressed
                        // see e.g. https://developer.mozilla.org/en-US/docs/Web/HTTP/Guides/MIME_types/Common_types
                        "application/x-zip-compressed": [".zip"],
                      }}
                      fullWidth
                      message="Upload ZIP or individual files by using drag and drop, or by clicking to browse."
                      dropzoneValidator={validFileName(s18Files.map(({ file }) => file))}
                      validate={requireAll(s18Files.length)}
                      getFilesFromEvent={getFilesFromEventWithFlattenedStructure}
                    />
                  </Box>
                  <RequiredFiles fieldName="Files" fileMap={s18Files} />
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "flex-end",
                      mt: 2,
                      position: "sticky",
                      bottom: 0,
                      backgroundColor: "white",
                      zIndex: 1,
                      py: 3,
                    }}
                  >
                    <Button type="button" data-testid="upload-cancel-button" color="primary" onClick={onClose} sx={{ mr: 1 }} disabled={submitting}>
                      Close
                    </Button>
                    <LoadingButton
                      type="submit"
                      data-testid="upload-save-button"
                      color="primary"
                      loading={submitting}
                      disabled={!dirty || !valid || submitting}
                    >
                      Upload
                    </LoadingButton>
                  </Box>
                </form>
              );
            }}
          />
        </Box>
      }
      onClose={onClose}
      DialogActionsProps={{ sx: { display: "none" } }}
    />
  );
};

export default UploadS18Modal;
